/**
 * loadCustomRunIntoLiveDoc — project a CUSTOM (build-on-an-existing-document)
 * run's sections into the live Word document.
 *
 * Why a dedicated path (not ``loadRunIntoLiveDoc``): a custom run's sections are
 * the writer's OWN heading outline, stored under synthetic ``CUSTOM-N`` module
 * ids. The template Load path scaffolds from the DOC-TYPE skeleton and fills by
 * matching each section's number against that skeleton — but a custom run has no
 * doc-type skeleton entries, so ``/all-sections`` returns ``section_number =
 * "CUSTOM-N"`` (the id, not "5.1"), every section is an orphan, nothing is
 * placed, and the force-clear at the top leaves the doc BLANK. That is exactly
 * the "Load doc emptied everything" bug for custom submissions.
 *
 * This loader instead scaffolds from ``custom_spine`` (the real number / title /
 * level the writer's document carried) and fills each heading with its
 * ``CUSTOM-N`` body, matched by id.
 *
 * The document's title-page block (captured before the first heading at import)
 * is restored as leading chrome above the first heading: the title line /
 * subtitle / confidentiality prose via ``insertLeadingContent``, and — when the
 * import captured a structured metadata table — the label/value fields via the
 * borderless ``updateTitlePageFields`` renderer (the same path a drafted title
 * page uses), NOT as a bordered markdown table.
 */

import { getAllSectionContent, getRunSnapshot } from "../../api/client";
import { splitDraftBySections } from "../../services/formatter/sectionSplitter";
import {
  insertLeadingContent,
  insertSectionContent,
  scaffoldDocument,
  updateTitlePageFields,
  type SectionHeading,
} from "../../services/wordWriter";
import { beginLiveDocLoad, endLiveDocLoad } from "../../services/liveDocLoadState";
import { runPostSectionCitationPipeline } from "./citationPipeline";
import { sanitizeDraftForWord } from "./draftSanitizer";
import type { LoadRunIntoLiveDocOptions, LoadRunIntoLiveDocResult } from "./loadRunIntoLiveDoc";

/** The scaffold placeholder written under an unfilled heading. A custom section
 *  whose persisted body is only this placeholder has no real content — skip it
 *  so Load leaves a clean empty heading rather than painting the placeholder. */
const SCAFFOLD_PLACEHOLDER = "Content not yet generated.";

/** Minimal custom-spine row shape this loader needs. ``level`` is optional — a
 *  custom spine mapped for display (``mapCustomSpineDTO``) drops it, so we
 *  derive the heading depth from the number's dot-count when it's absent. */
export interface CustomSpineRow {
  id: string;
  number: string;
  title: string;
  level?: number;
  /** True for the synthetic leading "Title Page" row — restored as leading
   *  chrome (no injected heading), not scaffolded as a numbered section. */
  isTitle?: boolean;
}

/** Heading depth from a dotted number: ``"5"`` → 1, ``"5.1"`` → 2,
 *  ``"5.1.1"`` → 3 (clamped to 3, the deepest scaffold level). A blank/odd
 *  number falls back to level 1. Used only when the spine row carries no level. */
function _levelFromNumber(number: string): 1 | 2 | 3 {
  const depth = (number || "").trim().split(".").filter(Boolean).length;
  return (depth >= 3 ? 3 : depth === 2 ? 2 : 1) as 1 | 2 | 3;
}

function _clampLevel(level: number | undefined, number: string): 1 | 2 | 3 {
  if (level === undefined || level < 1) return _levelFromNumber(number);
  return (level >= 3 ? 3 : level === 2 ? 2 : 1) as 1 | 2 | 3;
}

/** True when a persisted body is empty or only the scaffold placeholder. */
function _isEmptyOrPlaceholder(content: string | null | undefined): boolean {
  const trimmed = (content ?? "").trim();
  return trimmed.length === 0 || trimmed === SCAFFOLD_PLACEHOLDER;
}

export async function loadCustomRunIntoLiveDoc(
  runId: string,
  customSpine: ReadonlyArray<CustomSpineRow>,
  options?: LoadRunIntoLiveDocOptions,
): Promise<LoadRunIntoLiveDocResult> {
  console.log(`[loadCustomRunIntoLiveDoc] starting runId=${runId} spineRows=${customSpine.length}`);
  const sections = await getAllSectionContent(runId);
  console.log(
    `[loadCustomRunIntoLiveDoc] getAllSectionContent returned sectionCount=${sections.length} runId=${runId}`,
  );

  // Content keyed by the CUSTOM-N id (the module_id, which /all-sections also
  // echoes as section_number for a custom run).
  const contentById = new Map<string, string>();
  for (const sec of sections) {
    const id = sec.module_id || sec.section_number;
    if (id && sec.draft_content && !_isEmptyOrPlaceholder(sec.draft_content)) {
      contentById.set(id, sec.draft_content);
    }
  }

  // Abort before wiping the doc when NO section holds real content — a custom
  // run whose SSOT is all-placeholder (e.g. imported before it was filled) would
  // otherwise force-clear the live doc and paint nothing back, destroying the
  // writer's real Word content. Protect it instead.
  if (contentById.size === 0) {
    console.log(
      `[loadCustomRunIntoLiveDoc] ABORTED runId=${runId} reason=no_real_section_content spineRows=${customSpine.length}`,
    );
    throw new Error(
      `loadCustomRunIntoLiveDoc: run ${runId} has no non-placeholder section content — aborting to protect existing Word content`,
    );
  }

  // The synthetic leading title-page row (captured before the first heading at
  // import). It is NOT a numbered section — restore it as leading chrome after
  // the skeleton scaffolds, and keep it out of the heading skeleton so no
  // "Title Page" heading is injected into the writer's document.
  const titleRow = customSpine.find((row) => row.isTitle);
  const outlineRows = customSpine.filter((row) => !row.isTitle);

  beginLiveDocLoad();
  try {
    // Scaffold from the writer's own outline: real number + title + depth. The
    // title page is restored separately (below) as leading chrome; the outline
    // rows use literal numbers (a custom outline is not guaranteed to be a
    // positionally valid native-numbering sequence).
    const skeleton: SectionHeading[] = outlineRows.map((row) => ({
      number: row.number || "",
      title: row.title || row.id,
      level: _clampLevel(row.level, row.number),
    }));
    try {
      await scaffoldDocument({
        skeleton,
        documentTitle: "",
        forceClear: true,
        nativeNumbering: false,
        suppressHeadingNumbers: false,
      });
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      throw new Error(`loadCustomRunIntoLiveDoc: scaffoldDocument failed for run ${runId} — ${detail}`);
    }

    // Restore the title-page block as leading chrome (above the first heading,
    // no injected heading): first the title/subtitle/confidentiality prose from
    // the CUSTOM title body, then — when the import captured a structured
    // metadata table — the label/value fields as a BORDERLESS title-page table
    // (same renderer a drafted title page uses) rather than a bordered markdown
    // table. Older runs (no structured fields) fall back to the prose body alone,
    // which may still carry a markdown table.
    if (titleRow) {
      const titleBody = contentById.get(titleRow.id);
      const cleanedTitle = titleBody ? sanitizeDraftForWord(titleBody) : "";
      if (cleanedTitle) {
        try {
          await insertLeadingContent(cleanedTitle);
        } catch (err) {
          console.warn("[loadCustomRunIntoLiveDoc] title-page restore failed (non-fatal):", err);
        }
      }
      const titleSection = sections.find((s) => (s.module_id || s.section_number) === titleRow.id);
      const titleFields = titleSection?.title_fields;
      if (titleFields && Object.keys(titleFields).length > 0) {
        try {
          // "firstHeading" fallback: the outline is already scaffolded, so a
          // doc with no confidentiality anchor must place the table above the
          // first heading, not at document end.
          await updateTitlePageFields(titleFields, undefined, undefined, { anchorFallback: "firstHeading" });
        } catch (err) {
          console.warn("[loadCustomRunIntoLiveDoc] title-fields render failed (non-fatal):", err);
        }
      }
    }

    // One write job per spine row that has real content, keyed by its display
    // number (the bookmark scaffoldDocument planted). Numbers can repeat or be
    // blank in a hand-authored doc; splitDraftBySections keys off the number so
    // we pass each section's own number as the only sibling.
    const writeJobs: Array<{ number: string; chunk: string; title?: string }> = [];
    for (const row of outlineRows) {
      const body = contentById.get(row.id);
      if (!body) continue;
      const cleaned = sanitizeDraftForWord(body);
      if (!cleaned) continue;
      if (!row.number.trim()) {
        // No number → no bookmark and no numeric anchor (typical for an
        // imported Asthra doc, whose heading numbers live in native list
        // numbering absent from the captured text). Write the whole body as one
        // job and anchor it by the heading TITLE (insertSectionContent's
        // title-match fallback). Don't splitDraftBySections — it keys off the
        // number and can't chunk a blank one. Non-fatal if the title misses.
        writeJobs.push({ number: "", chunk: cleaned, title: row.title });
        continue;
      }
      const chunks = Object.entries(splitDraftBySections(cleaned, [row.number], row.number, [])).filter(
        ([, c]) => c.trim().length > 0,
      );
      for (const [secNum, chunk] of chunks) {
        writeJobs.push({ number: secNum, chunk });
      }
    }

    let completed = 0;
    const total = writeJobs.length;
    const emitProgress = () => {
      const percent = total === 0 ? 100 : Math.round((completed / total) * 100);
      options?.onProgress?.({ completed, total, percent });
    };
    emitProgress();

    let written = 0;
    for (const { number, chunk, title } of writeJobs) {
      try {
        const ok = await insertSectionContent({
          sectionNumber: number,
          content: chunk,
          scrollAfterInsert: false,
          // Title-anchor fallback for blank-number rows (imported Asthra docs);
          // undefined for numbered rows, so their behaviour is unchanged.
          ...(title ? { sectionTitle: title } : {}),
        });
        if (ok) {
          written += 1;
          // The citation pipeline keys off a section number; skip it for blank-
          // number rows (nothing to anchor its scan to).
          if (number.trim()) await runPostSectionCitationPipeline(number);
        }
      } catch (err) {
        const label = number.trim() ? `§${number}` : `“${title ?? ""}”`;
        console.warn(`[loadCustomRunIntoLiveDoc] insertSectionContent failed for ${label} (non-fatal):`, err);
      } finally {
        completed += 1;
        emitProgress();
      }
    }

    console.log(
      `[loadCustomRunIntoLiveDoc] done runId=${runId} sectionsWritten=${written}/${total} spineRows=${customSpine.length}`,
    );
    return { sectionsWritten: written, sectionsAttempted: total, scaffolded: true, orphanDetail: null };
  } finally {
    endLiveDocLoad();
  }
}

/**
 * Load dispatcher: if ``runId`` is a custom submission, load it via the custom
 * sequence above and return the result; otherwise return ``null`` so the caller
 * runs its normal template Load path. Centralises the "is this custom?" check
 * for every Load entry point (spine "Load doc", Home card, Drafts panel) so the
 * destructive whole-file hydrate is never used on a custom run.
 *
 * The snapshot fetch is the same one the shell already makes on reopen, so this
 * adds one round-trip only on the Load click. A snapshot failure returns ``null``
 * (fall back to the template path) rather than throwing.
 */
export async function loadCustomRunIfApplicable(
  runId: string,
  options?: LoadRunIntoLiveDocOptions,
): Promise<LoadRunIntoLiveDocResult | null> {
  const snapshot = await getRunSnapshot(runId).catch((err) => {
    console.warn("[loadCustomRunIfApplicable] getRunSnapshot failed (non-fatal):", err);
    return null;
  });
  if (!snapshot || snapshot.submission_mode !== "custom") return null;
  const customSpine: CustomSpineRow[] = (snapshot.spine ?? []).map((s) => ({
    id: s.id,
    number: s.number,
    title: s.title,
    isTitle: s.is_title ?? undefined,
  }));
  return loadCustomRunIntoLiveDoc(runId, customSpine, options);
}
